import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField } from '@mui/material';

interface TripPricingCalculatorProps {
  selectedFleetInfo: any;
  serviceType: string | null;
  calculatedDays: number;
  customKm?: number;
  totalDistance?:number | null;
  onDiscountedPriceChange?: (price: number) => void;
  onDistancePriceCalculationChange?: (price: number | null) => void;
  onEstimatedPriceChange?: (price: number) => void;
}

const TripPricingCalculator: React.FC<TripPricingCalculatorProps> = ({
  selectedFleetInfo,
  serviceType,
  calculatedDays,
  totalDistance,
  customKm = 0,
  onDiscountedPriceChange,
  onDistancePriceCalculationChange,
  onEstimatedPriceChange
}) => {
  const [customNegotiationPrice, setCustomNegotiationPrice] = useState<number | null>(null);
  const [customBasePricePerKm, setCustomBasePricePerKm] = useState<number | null>(null);
  const [exceededDistanceAmount, setExceededDistanceAmount] = useState<number | null>(null);

  // Calculate custom daily price based on basePricePerKm and kmLimitPerDay
  const calculateCustomDailyPrice = () => {
    if (customBasePricePerKm !== null && selectedFleetInfo?.kmLimitPerDay) {
      return customBasePricePerKm * selectedFleetInfo.kmLimitPerDay;
    }
    return null;
  };

  const customDailyPrice = calculateCustomDailyPrice();
  
  useEffect(() => {
    if (!selectedFleetInfo || !onEstimatedPriceChange) return;

    let estimatedPrice = 0;

    if (serviceType === 'AIRPORT' || serviceType === 'ONEWAY' || serviceType === 'LOCAL' || serviceType === 'PERPAX') {
      estimatedPrice = (calculatedDays * (selectedFleetInfo.offerPrice)) + (calculatedDays * (selectedFleetInfo.driverAllowance || 0));
    } else {
      // Outstation services
      estimatedPrice = (selectedFleetInfo.displayPrice * calculatedDays) + 
                      ((selectedFleetInfo.driverAllowance || 300) * calculatedDays) + 
                      (customKm * (selectedFleetInfo.ratePerExtraKm || 13));
    }

    onEstimatedPriceChange(estimatedPrice);
  }, [selectedFleetInfo, serviceType, calculatedDays, customKm, onEstimatedPriceChange]);

  // Calculate the amount when total distance exceeds km limit per day
  // useEffect(() => {
  //   if (!selectedFleetInfo || !totalDistance || serviceType !== 'OUTSTATION') return;
    
  //   const kmLimitPerDay = selectedFleetInfo.kmLimitPerDay || 0;
  //   const totalKmLimit = kmLimitPerDay * calculatedDays;
    
  //   if (totalDistance > totalKmLimit) {
  //     const basePricePerKm = selectedFleetInfo.basePricePerKm || 0;
  //     const driverAllowance = selectedFleetInfo.driverAllowance || 300;
      
  //     const newAmount = (totalDistance * basePricePerKm) + (driverAllowance * calculatedDays);
  //     setExceededDistanceAmount(newAmount);
  //   } else {
  //     setExceededDistanceAmount(null);
  //   }
  // }, [selectedFleetInfo, totalDistance, calculatedDays, serviceType]);

  // Emit distance price calculation changes
  useEffect(() => {
    if (onDistancePriceCalculationChange) {
      onDistancePriceCalculationChange(exceededDistanceAmount);
    }
  }, [exceededDistanceAmount, onDistancePriceCalculationChange]);

  // Calculate discounted price and emit changes - MOVED BEFORE EARLY RETURN
  useEffect(() => {
    if (!selectedFleetInfo || !onDiscountedPriceChange) return;

    let discountedPrice = 0;

    if (serviceType === 'AIRPORT' || serviceType === 'ONEWAY' || serviceType === 'LOCAL' || serviceType === 'PERPAX') {
      discountedPrice = selectedFleetInfo.offerPrice;
    } else {
      // Outstation services
      let basePrice;
      if (customDailyPrice !== null) {
        basePrice = customDailyPrice * calculatedDays;
      } else {
        const price = customNegotiationPrice !== null ? customNegotiationPrice : selectedFleetInfo.negotiationPrice;
        basePrice = price * calculatedDays;
      }
      
      discountedPrice = basePrice + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays) + (customKm * (selectedFleetInfo.ratePerExtraKm || 13));
    }

    onDiscountedPriceChange(discountedPrice);
  }, [selectedFleetInfo, serviceType, calculatedDays, customKm, customNegotiationPrice, customBasePricePerKm, customDailyPrice, onDiscountedPriceChange]);

  if (!selectedFleetInfo) return null;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {/* Vehicle Header with Price Calculation */}
      <Box sx={{ 
        backgroundColor: '#1976d2', 
        color: 'white', 
        p: 1.5, 
        borderRadius: '4px 4px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Price Calculation
          </Typography>
        </Box>
      </Box>

      {/* Airport, Oneway, and Local Services - Single Column Layout */}
      {(serviceType === 'AIRPORT' || serviceType === 'ONEWAY' || serviceType === 'LOCAL' || serviceType === 'PERPAX') ? (
        <Box sx={{ 
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderTop: 'none'
        }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ color: '#212529', mb: 1.5, fontSize: '1rem', fontWeight: 'bold' }}>
              {serviceType === 'AIRPORT' ? 'Service - Airport Transfer' :
               serviceType === 'ONEWAY' ? 'Service - One Way Trip' :
               serviceType === 'PERPAX' ? 'Service - Per Pax Trip' :
               'Price Details - Pune Local Service'}
            </Typography>
            
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#495057' }}>Price:</Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                  ₹{selectedFleetInfo.displayPrice}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#495057' }}>Offer Price:</Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                  ₹{selectedFleetInfo.offerPrice}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ minWidth: 'fit-content', color: '#495057' }}>Negotiation Price:</Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                  ₹{selectedFleetInfo.negotiationPrice}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#28a745', fontSize: '0.85rem', fontWeight: 'bold', minWidth: 'fit-content' }}>
                  Included Expenses:
                </Typography>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem' }}>
                  {String(selectedFleetInfo.includedExpenses || 'None')
                    .split(',')
                    .map((item: string) => item.trim())
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#dc3545', fontSize: '0.85rem', fontWeight: 'bold', minWidth: 'fit-content' }}>
                  Excluded Expenses:
                </Typography>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem' }}>
                  {String(selectedFleetInfo.excludedExpenses || 'None')
                    .split(',')
                    .map((item: string) => item.trim())
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#495057', minWidth: 'fit-content' }}>
                      Driver Allowance
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                      ₹{selectedFleetInfo.driverAllowance}
                    </Typography>
                  </Box>
              
              {serviceType === 'LOCAL' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#495057', minWidth: 'fit-content' }}>
                      Slot:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                      {selectedFleetInfo.slot || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#495057', minWidth: 'fit-content' }}>
                      Km Limit Per Day:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                      {selectedFleetInfo.kmLimitPerDay || 'N/A'} km
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            
            {/* Add styled total for single column services */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              mt: 1.5, 
              pt: 1.5, 
              pb: 0.5,
              borderTop: '1px solid #28a745',
              backgroundColor: 'rgba(40, 167, 69, 0.1)',
              borderRadius: selectedFleetInfo.offerPrice < selectedFleetInfo.displayPrice ? '4px 4px 0 0' : '0 0 4px 4px',
              px: 1.5
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#495057', 
                fontSize: '1rem'
              }}>
                Total Price:
              </Typography>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#28a745',
                fontSize: '1rem'
              }}>
                {(() => {
                  const servicePrice = (selectedFleetInfo.offerPrice + (selectedFleetInfo.driverAllowance || 0)) * calculatedDays;
                  const formattedPrice = servicePrice.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  });
                  
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: '#4caf50', 
                          fontSize: '1.1rem'
                        }}>
                          ₹{formattedPrice}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Typography>
            </Box>

            {/* Customer Savings for single column services - Only show if offer price is less than display price */}
            {selectedFleetInfo.offerPrice < selectedFleetInfo.displayPrice && (
              <Box sx={{ 
                backgroundColor: 'rgba(40, 167, 69, 0.05)', 
                px: 1.5,
                py: 0.8,
                borderRadius: '0 0 4px 4px',
                borderTop: '1px solid rgba(40, 167, 69, 0.2)'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 'medium', 
                    color: '#28a745',
                    fontSize: '0.85rem',
                    textAlign: 'center'
                  }}>
                    Customer Savings: ₹{(() => {
                      const savings = selectedFleetInfo.displayPrice - selectedFleetInfo.offerPrice;
                      const savingsPercent = Math.round(((selectedFleetInfo.displayPrice - selectedFleetInfo.offerPrice) / selectedFleetInfo.displayPrice) * 100);
                      return `${savings}.00 (${savingsPercent}% off)`;
                    })()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        /* Outstation Services - Two Column Layout with Minimum Price */
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderTop: 'none'
        }}>
          {/* Regular Price Calculation */}
          <Box sx={{ 
            p: 2, 
            borderRight: '1px solid #dee2e6'
          }}>
            <Typography variant="h6" sx={{ color: '#212529', mb: 1.5, fontSize: '1rem', fontWeight: 'bold' }}>
              Price Details: (Round Trip)
            </Typography>
            
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#495057' }}>Price:</Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                  ₹{selectedFleetInfo.displayPrice} x {calculatedDays} = ₹{selectedFleetInfo.displayPrice * calculatedDays}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#495057' }}>Driver Allowance:</Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontWeight: 'medium' }}>
                  ₹{selectedFleetInfo.driverAllowance} x {calculatedDays} = ₹{(selectedFleetInfo.driverAllowance) * calculatedDays}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, backgroundColor: 'rgba(108, 117, 125, 0.1)', p: 0.5, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  Base Price Per Kilometer  : <span style={{ color: '#212529', fontWeight: 'bold' }}>₹ {selectedFleetInfo.basePricePerKm}</span>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#28a745', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  Included Expenses:
                </Typography>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem' }}>
                  {String(selectedFleetInfo.includedExpenses || 'None')
                    .split(',')
                    .map((item: string) => item.trim())
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#dc3545', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  Excluded Expenses:
                </Typography>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem' }}>
                  {String(selectedFleetInfo.excludedExpenses || 'None')
                    .split(',')
                    .map((item: string) => item.trim())
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
            </Box>
            
           
            {/* Add styled total for estimated price */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 1.5, 
              pt: 1.5, 
              pb: 0.5,
              borderTop: '1px solid #28a745',
              backgroundColor: 'rgba(40, 167, 69, 0.1)',
              borderRadius: '0 0 4px 4px',
              px: 1.5
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#495057', 
                fontSize: '1rem'
              }}>
                Total Estimated Price:
              </Typography>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#28a745',
                fontSize: '1rem'
              }}>
                {(() => {
                  const estimatedTotal = (selectedFleetInfo.displayPrice * calculatedDays) + 
                                        ((selectedFleetInfo.driverAllowance || 300) * calculatedDays) + 
                                        (customKm * (selectedFleetInfo.ratePerExtraKm || 13));
                  const formattedPrice = estimatedTotal.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  });
                  
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: '#4caf50', 
                          fontSize: '1.1rem'
                        }}>
                          ₹{formattedPrice}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Typography>
            </Box>
             {/* Display new amount when total distance exceeds km limit per day */}
             {/* {exceededDistanceAmount !== null && serviceType === 'OUTSTATION' && totalDistance && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 1.5, 
                pt: 1, 
                pb: 1,
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                borderRadius: '4px',
                px: 1.5,
                border: '1px dashed #28a745'
              }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  color: '#495057', 
                  fontSize: '0.9rem'
                }}>
                  Distance Price Calculation:
                  <Typography variant="caption" sx={{ display: 'block', color: '#212529', mt: 0.5 }}>
                    ({totalDistance} km × ₹{selectedFleetInfo.basePricePerKm}) + (₹{selectedFleetInfo.driverAllowance || 300} × {calculatedDays} days)
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 'bold', 
                  color: '#28a745',
                  fontSize: '0.9rem'
                }}>
                  ₹{exceededDistanceAmount.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  })}
                </Typography>
              </Box>
            )} */}
            
          </Box>

          {/* Minimum Price Calculation */}
          <Box sx={{ 
            p: 2,
            backgroundColor: '#f1f3f4'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 'bold', 
                color: '#212529', 
                fontSize: '1rem'
              }}>
                Discounted Price Calculation:
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.8rem', fontWeight: 'bold', mb: 0.5 }}>
                  Custom Price
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Enter custom price"
                  value={customNegotiationPrice !== null ? customNegotiationPrice : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    // Prevent negative values
                    if (value !== null && value < 0) return;
                    setCustomNegotiationPrice(value);
                  }}
                  disabled={customBasePricePerKm !== null}
                  InputProps={{
                    startAdornment: <span style={{ color: '#495057', marginRight: '4px', fontWeight: 'bold' }}>₹</span>,
                    inputProps: { min: 0 } // Set minimum value to 0
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: customBasePricePerKm !== null ? 'rgba(128,128,128,0.1)' : 'rgba(255,255,255,0.8)',
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: customBasePricePerKm !== null ? 'rgba(128,128,128,0.3)' : '#ced4da',
                      },
                      '&:hover fieldset': {
                        borderColor: customBasePricePerKm !== null ? 'rgba(128,128,128,0.3)' : '#adb5bd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: customBasePricePerKm !== null ? 'rgba(128,128,128,0.3)' : '#495057',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: customBasePricePerKm !== null ? 'rgba(128,128,128,0.7)' : '#212529',
                      fontSize: '0.85rem',
                      padding: '8px 10px',
                      fontWeight: 'bold'
                    },
                    // Hide number input arrows and disable scroll wheel
                    '& input[type=number]': {
                      '-moz-appearance': 'textfield',
                      '&::-webkit-outer-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                      '&::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                    },
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              </Box>
              
              {/* OR separator */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minWidth: '30px',
                mt: 2.5
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#fff', 
                  fontSize: '0.9rem', 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  OR
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.8rem', fontWeight: 'bold', mb: 0.5 }}>
                  Custom Km Rate
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Enter rate per km"
                  value={customBasePricePerKm !== null ? customBasePricePerKm : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    // Prevent negative values
                    if (value !== null && value < 0) return;
                    setCustomBasePricePerKm(value);
                  }}
                  disabled={customNegotiationPrice !== null}
                  InputProps={{
                    startAdornment: <span style={{ color: '#495057', marginRight: '4px', fontWeight: 'bold' }}>₹</span>,
                    inputProps: { min: 0 } // Set minimum value to 0
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: customNegotiationPrice !== null ? 'rgba(128,128,128,0.1)' : 'rgba(76,175,80,0.1)',
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: customNegotiationPrice !== null ? 'rgba(128,128,128,0.3)' : 'rgba(76,175,80,0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: customNegotiationPrice !== null ? 'rgba(128,128,128,0.3)' : 'rgba(76,175,80,0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: customNegotiationPrice !== null ? 'rgba(128,128,128,0.3)' : '#4caf50',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: customNegotiationPrice !== null ? 'rgba(128,128,128,0.7)' : '#212529',
                      fontSize: '0.85rem',
                      padding: '8px 10px',
                      fontWeight: 'bold'
                    },
                    // Hide number input arrows and disable scroll wheel
                    '& input[type=number]': {
                      '-moz-appearance': 'textfield',
                      '&::-webkit-outer-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                      '&::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                      },
                    },
                  }}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                />
              </Box>
            </Box>

            {/* Show calculation preview when customBasePricePerKm is entered */}
            {customBasePricePerKm !== null && selectedFleetInfo.kmLimitPerDay && (
              <Box sx={{ 
                backgroundColor: 'rgba(76,175,80,0.1)', 
                p: 1, 
                borderRadius: 1, 
                border: '1px solid rgba(76,175,80,0.3)',
                mb: 1
              }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  Custom Calculation: ₹{customBasePricePerKm} × {selectedFleetInfo.kmLimitPerDay} km = ₹{customDailyPrice} per day
                </Typography>
              </Box>
            )}
            
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.8)', 
              p: 1.5, 
              borderRadius: 1, 
              border: '1px solid #ced4da',
              mb: 1.5,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 'bold', 
                color: '#212529', 
                mb: 1,
                fontSize: '0.9rem',
                borderBottom: '1px dashed rgba(108, 117, 125, 0.3)',
                paddingBottom: '4px'
              }}>
                Price Details: (Round Trip)
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8, backgroundColor: 'rgba(108, 117, 125, 0.1)', p: 0.8, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.9rem', fontWeight: 'medium' }}>
                  Minimum Price:
                </Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  ₹{(() => {
                    // Use custom daily price if available, otherwise use original logic
                    if (customDailyPrice !== null) {
                      return `${customDailyPrice} × ${calculatedDays} = ₹${customDailyPrice * calculatedDays}`;
                    } else {
                      const price = customNegotiationPrice !== null ? customNegotiationPrice : selectedFleetInfo.negotiationPrice;
                      return `${price} × ${calculatedDays} = ₹${price * calculatedDays}`;
                    }
                  })()}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8, backgroundColor: 'rgba(108, 117, 125, 0.1)', p: 0.8, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.9rem', fontWeight: 'medium' }}>
                  Driver Allowance:
                </Typography>
                <Typography variant="body2" sx={{ color: '#212529', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  ₹{selectedFleetInfo.driverAllowance} × {calculatedDays} = ₹{(selectedFleetInfo.driverAllowance) * calculatedDays}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, mt: 0.5, borderTop: '1px dashed rgba(40, 167, 69, 0.3)' }}>
                <Typography variant="body2" sx={{ color: '#495057', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  Total Estimated Minimum Price:
                </Typography>
                <Typography variant="body2" sx={{ color: '#28a745', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  ₹{(() => {
                    // Calculate total using custom daily price if available
                    let basePrice;
                    if (customDailyPrice !== null) {
                      basePrice = customDailyPrice * calculatedDays;
                    } else {
                      const price = customNegotiationPrice !== null ? customNegotiationPrice : selectedFleetInfo.negotiationPrice;
                      basePrice = price * calculatedDays;
                    }
                    return basePrice + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays) + (customKm * (selectedFleetInfo.ratePerExtraKm || 13));
                  })()}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 1.5, 
              pt: 1.5, 
              pb: 0.5,
              borderTop: '1px solid rgba(76, 175, 80, 0.5)',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '4px 4px 0 0',
              px: 1.5
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#495057', 
                fontSize: '1rem'
              }}>
                Discounted Price:
              </Typography>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 'bold', 
                color: '#ff9800',
                fontSize: '1rem',
                textShadow: '0 1px 1px rgba(0,0,0,0.1)'
              }}>
                {(() => {
                  // Calculate final total using custom daily price if available
                  let basePrice;
                  if (customDailyPrice !== null) {
                    basePrice = customDailyPrice * calculatedDays;
                  } else {
                    const price = customNegotiationPrice !== null ? customNegotiationPrice : selectedFleetInfo.negotiationPrice;
                    basePrice = price * calculatedDays;
                  }
                  
                  const negotiationTotal = basePrice + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays) + (customKm * (selectedFleetInfo.ratePerExtraKm || 13));
                  const formattedPrice = negotiationTotal.toLocaleString('en-IN', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                  });
                  
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: '#4caf50', 
                          fontSize: '1.1rem'
                        }}>
                          ₹{formattedPrice}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}
              </Typography>
            </Box>
            
            {/* Customer Savings - Below discounted price */}
            <Box sx={{ 
              backgroundColor: 'rgba(76, 175, 80, 0.08)', 
              px: 1.5,
              py: 0.8,
              borderRadius: '0 0 4px 4px',
              borderTop: '1px solid rgba(76, 175, 80, 0.3)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'medium', 
                  color: '#4caf50',
                  fontSize: '0.85rem',
                  textAlign: 'center'
                }}>
                  Customer Savings: ₹{(() => {
                    const displayTotal = (selectedFleetInfo.displayPrice * calculatedDays) + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays);
                    
                    let minimumTotal;
                    if (customDailyPrice !== null) {
                      minimumTotal = (customDailyPrice * calculatedDays) + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays);
                    } else {
                      const price = customNegotiationPrice !== null ? customNegotiationPrice : selectedFleetInfo.negotiationPrice;
                      minimumTotal = (price * calculatedDays) + ((selectedFleetInfo.driverAllowance || 300) * calculatedDays);
                    }
                    
                    const savings = displayTotal - minimumTotal;
                    const savingsPercent = Math.round(((displayTotal - minimumTotal) / displayTotal) * 100);
                    
                    return `${savings}.00 (${savingsPercent}% off)`;
                  })()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TripPricingCalculator;
